import Phaser from "phaser";

interface PlaceholderSpec {
  key: string;
  width: number;
  height: number;
  /** Короткая подпись для удобной идентификации в кадре */
  label: string;
  /** Опциональный фоновый цвет (по умолчанию серый плейсхолдер) */
  fill?: number;
}

/**
 * Приглушённая палитра, чтобы плейсхолдеры разных сущностей не сливались
 * друг с другом и с фоном арены, но и не били по глазам. Все цвета
 * подобраны как «pastel pixel art»: средний lightness, низкая насыщенность.
 */
const PALETTE = {
  goldie: 0xd4a574, // приглушённый золотисто-рыжий
  blackie: 0x6b6b7a, // тёмно-серый с холодным оттенком
  lilith: 0x9a7eb0, // приглушённый лиловый
  succuba: 0xb87e6e, // мягкий тёрракотовый
  fxPerform: 0xc89bd4, // розово-сиреневый
  fxHit: 0xe0a8b8, // мягкий розовый
  fxHeart: 0xd99bb0, // насыщенно-розовый, но не кислотный
  fxPuff: 0xc8b8d4, // дымчатый сиреневый
  bgArena: 0x1a1a2e, // ночное небо
  platformPlayer: 0x6e7e5e, // травянистый болотный
  platformEnemy: 0x7e8e6e, // тот же оттенок чуть светлее
  uiBox: 0xe8e0c8, // кремовая бумага
  hpFill: 0x6cb86c, // приглушённый зелёный
  cursor: 0x333333,
} as const;

const CATBOY_POSES = [
  "idle_1",
  "idle_2",
  "kneel",
  "crawl_1",
  "crawl_2",
  "purr",
  "beg",
  "worry",
  "blush_1",
  "blush_2",
  "run_1",
  "run_2",
] as const;

const GIRL_POSES = ["idle_1", "idle_2", "command", "smug", "blush_1", "blush_2", "run_1", "run_2"] as const;

function catboyList(prefix: string, fill: number): PlaceholderSpec[] {
  return CATBOY_POSES.map((p) => {
    const isCrawl = p.startsWith("crawl");
    return {
      key: `${prefix}_${p}`,
      width: isCrawl ? 64 : 48,
      height: isCrawl ? 48 : 64,
      label: `${prefix}\n${p}`,
      fill,
    };
  });
}

function girlList(prefix: string, fill: number): PlaceholderSpec[] {
  return GIRL_POSES.map((p) => ({
    key: `${prefix}_${p}`,
    width: 96,
    height: 128,
    label: `${prefix.replace("_back", "").replace("_front", "")}\n${p}`,
    fill,
  }));
}

export const PLACEHOLDERS: PlaceholderSpec[] = [
  ...catboyList("goldie", PALETTE.goldie),
  ...catboyList("blackie", PALETTE.blackie),
  ...girlList("lilith_back", PALETTE.lilith),
  ...girlList("succuba_front", PALETTE.succuba),

  { key: "fx_perform_1", width: 32, height: 32, label: "perform1", fill: PALETTE.fxPerform },
  { key: "fx_perform_2", width: 32, height: 32, label: "perform2", fill: PALETTE.fxPerform },
  { key: "fx_hit_1", width: 48, height: 48, label: "hit1", fill: PALETTE.fxHit },
  { key: "fx_hit_2", width: 48, height: 48, label: "hit2", fill: PALETTE.fxHit },
  { key: "fx_heart_1", width: 16, height: 16, label: "h1", fill: PALETTE.fxHeart },
  { key: "fx_heart_2", width: 16, height: 16, label: "h2", fill: PALETTE.fxHeart },
  { key: "fx_pokeball_puff", width: 64, height: 64, label: "puff", fill: PALETTE.fxPuff },

  { key: "bg_arena", width: 480, height: 200, label: "bg_arena", fill: PALETTE.bgArena },
  { key: "platform_player", width: 160, height: 24, label: "platform_player", fill: PALETTE.platformPlayer },
  { key: "platform_enemy", width: 120, height: 20, label: "platform_enemy", fill: PALETTE.platformEnemy },

  { key: "ui_hpbox_player", width: 160, height: 40, label: "ui_hpbox_player", fill: PALETTE.uiBox },
  { key: "ui_hpbox_enemy", width: 160, height: 40, label: "ui_hpbox_enemy", fill: PALETTE.uiBox },
  { key: "ui_hpbar_fill", width: 100, height: 6, label: "", fill: PALETTE.hpFill },
  { key: "ui_dialog_box", width: 480, height: 70, label: "ui_dialog_box", fill: PALETTE.uiBox },
  { key: "ui_menu_box", width: 200, height: 70, label: "ui_menu_box", fill: PALETTE.uiBox },
  { key: "ui_cursor", width: 8, height: 8, label: "", fill: PALETTE.cursor },
];

/** Светлый фон → чёрный текст; тёмный → белый, чтобы подпись всегда читалась. */
function pickLabelColor(fill: number): string {
  const r = (fill >> 16) & 0xff;
  const g = (fill >> 8) & 0xff;
  const b = fill & 0xff;
  // Перцептивная яркость в духе Rec. 601.
  const lum = (r * 299 + g * 587 + b * 114) / 1000;
  return lum > 140 ? "#000000" : "#ffffff";
}

export default class BootScene extends Phaser.Scene {
  constructor() {
    super("BootScene");
  }

  preload(): void {
    for (const spec of PLACEHOLDERS) {
      this.makePlaceholder(spec);
    }
  }

  create(): void {
    this.scene.start("BattleScene");
  }

  private makePlaceholder(spec: PlaceholderSpec): void {
    if (this.textures.exists(spec.key)) return;

    const fill = spec.fill ?? 0xcccccc;
    const g = this.make.graphics({ x: 0, y: 0 }, false);

    g.fillStyle(fill, 1);
    g.fillRect(0, 0, spec.width, spec.height);
    g.lineStyle(1, 0x000000, 1);
    g.strokeRect(0, 0, spec.width, spec.height);

    g.generateTexture(spec.key, spec.width, spec.height);
    g.destroy();

    if (spec.label) {
      this.attachLabel(spec, fill);
    }
  }

  /**
   * Дорисовать подпись поверх уже сгенерированной текстуры через RenderTexture.
   * Используем resolution=4, чтобы текст не размывался при scale FIT.
   */
  private attachLabel(spec: PlaceholderSpec, fill: number): void {
    const tempImg = this.add.image(0, 0, spec.key).setOrigin(0, 0);
    tempImg.setVisible(false);

    const text = this.add
      .text(spec.width / 2, spec.height / 2, spec.label, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: "8px",
        color: pickLabelColor(fill),
        align: "center",
        resolution: 1,
      })
      .setOrigin(0.5, 0.5);
    text.setVisible(false);

    const rt = this.add.renderTexture(0, 0, spec.width, spec.height);
    rt.setVisible(false);
    rt.draw(tempImg, 0, 0);
    rt.draw(text, spec.width / 2, spec.height / 2);

    this.textures.remove(spec.key);
    rt.saveTexture(spec.key);

    tempImg.destroy();
    text.destroy();
  }
}
